import { useCallback, useRef, useState } from 'react';

import Input from '@cartwhl/ui/atoms/Input';
import ToggleGroup from '@cartwhl/ui/atoms/ToggleGroup';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import FilesList from '@cartwhl/ui/input/FilesList';
import type {
  FilesListFile,
  FilesListHandle,
} from '@cartwhl/ui/input/FilesList';

import type { UploadResult, UploadStatus } from '../adminAssetUploadStore';
import { getUploadCdnBase, useStore } from '../adminAssetUploadStore';
import { ALLOWED_MIME_TYPES, FALLBACK_EXTENSIONS } from '../constants';
import FilesListDragOverlay from './FilesListDragOverlay';
import FilesListEmptyState from './FilesListEmptyState';
import FilesListItem from './FilesListItem';
import PluginTrackingSection from './PluginTrackingSection';
import ResultItem from './ResultItem';

interface FilesListItemWithStatusProps {
  file: FilesListFile;
  removeFile: () => void;
  uploadStatus: UploadStatus;
  results: UploadResult[];
}

function FilesListItemWithStatus({
  file,
  removeFile,
  uploadStatus,
  results,
}: FilesListItemWithStatusProps) {
  const isUploaded = results.some((r) => r.fileName === file.file.name);
  const isUploadingThisFile =
    uploadStatus.phase === 'uploading' &&
    uploadStatus.currentFileName === file.file.name;
  const status = isUploaded
    ? 'uploaded'
    : isUploadingThisFile
      ? 'uploading'
      : 'idle';
  return <FilesListItem file={file} removeFile={removeFile} status={status} />;
}

export type ResultView = 'urls' | 'paths' | 'filenames';

export default function AdminAssetUpload() {
  const store = useStore();
  const filesListRef = useRef<FilesListHandle>(null);
  const [selectedView, setSelectedView] = useState<ResultView>('filenames');
  const { prefix, fileIds, uploadStatus, results } = store.state;

  const handleFilesChange = useCallback(
    (newFiles: FilesListFile[]) => {
      store.syncFiles(newFiles);
    },
    [store],
  );

  const getFilesListRef = useCallback(() => filesListRef.current, []);
  store.setFilesListRef(getFilesListRef);

  const handleUpload = useCallback(() => {
    store.uploadAll();
  }, [store]);

  const handleClearAll = useCallback(() => {
    store.clearAll();
  }, [store]);

  const canUpload = fileIds.length > 0 && uploadStatus.phase === 'idle';
  const isUploading = uploadStatus.phase === 'uploading';
  const uploadCdnBase = getUploadCdnBase();

  return (
    <div className="min-h-screen">
      <div className="container mx-auto py-12">
        <div className="mb-5 flex flex-col gap-2">
          <Text
            variant="studio/display/xl"
            className="font-display text-primary text-[40px] leading-[48px]"
            as="h1"
          >
            Asset Upload
          </Text>
          <Text variant="studio/body/medium/24" className="text-tertiary">
            Upload images and videos for blog and website.
          </Text>
        </div>

        <div className="bg-secondary rounded-2xl p-4">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
            <div className="bg-primary border-primary flex flex-col gap-6 rounded-t-lg border p-8 lg:rounded-none lg:rounded-l-lg lg:pr-6">
              <div className="flex items-center justify-between">
                <Text
                  variant="studio/body/medium/24"
                  className="text-primary"
                  as="h2"
                >
                  <span className="mr-2">1.</span>Add your files
                </Text>
                {fileIds.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Text
                      variant="studio/body/medium/xs"
                      className="text-primary px-3 py-1"
                    >
                      {fileIds.length} {fileIds.length === 1 ? 'file' : 'files'}
                    </Text>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleClearAll}
                    >
                      Clear all
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Text
                  variant="studio/body/medium/sm"
                  className="text-primary"
                  as="label"
                >
                  Resource path prefix (e.g. share/my-folder/)
                </Text>
                <Input
                  name="resourcePathPrefix"
                  placeholder="share/"
                  value={prefix}
                  onChange={(e) => store.setPrefix(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <FilesList
                  ref={filesListRef}
                  allowedMimeTypes={ALLOWED_MIME_TYPES}
                  fallbackExtensions={FALLBACK_EXTENSIONS}
                  onFilesChange={handleFilesChange}
                  renderItem={(file, removeFile) => (
                    <FilesListItemWithStatus
                      file={file}
                      removeFile={removeFile}
                      uploadStatus={uploadStatus}
                      results={results}
                    />
                  )}
                  emptyStateContent={<FilesListEmptyState />}
                  dragOverlayContent={<FilesListDragOverlay />}
                />
              </div>
            </div>

            <div className="bg-primary border-primary flex flex-col gap-6 rounded-b-lg border-r border-b border-l p-8 lg:rounded-none lg:rounded-r-lg lg:border-t lg:border-l-0">
              <Text
                variant="studio/body/medium/24"
                className="text-primary"
                as="h2"
              >
                <span className="mr-2">2.</span>Results
              </Text>

              <ToggleGroup
                stretch
                items={[
                  { label: 'URLs', value: 'urls', content: <span>URLs</span> },
                  {
                    label: 'Paths',
                    value: 'paths',
                    content: <span>Paths</span>,
                  },
                  {
                    label: 'Filenames',
                    value: 'filenames',
                    content: <span>Filenames</span>,
                  },
                ]}
                value={selectedView}
                onValueChange={(value) => setSelectedView(value as ResultView)}
              />

              {isUploading && (
                <Text variant="studio/body/medium/sm" className="text-tertiary">
                  Uploading {uploadStatus.current} of {uploadStatus.total}...
                </Text>
              )}

              {!isUploading && results.length === 0 && (
                <Text variant="studio/body/medium/sm" className="text-tertiary">
                  Uploaded files will appear here.
                </Text>
              )}

              {results.length > 0 && (
                <ul className="flex flex-col gap-4">
                  {[...results].reverse().map((r, idx) => (
                    <ResultItem
                      key={`${r.resourcePath}-${idx}`}
                      result={r}
                      view={selectedView}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <Text variant="studio/body/medium/sm" className="text-tertiary">
              Uploading to{' '}
              <span className="text-primary font-mono">{uploadCdnBase}</span>
            </Text>
            <Button
              variant="primary"
              size="lg"
              disabled={!canUpload}
              onClick={handleUpload}
            >
              Upload all
            </Button>
          </div>
        </div>

        <PluginTrackingSection />
      </div>
    </div>
  );
}
